<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .search-page
    // Header with back button and title
    .search-header
      .d-flex.align-items-center.gap-2
        button.back-button(@click="handleBack")
          img(src="@/assets/icons/chevron-right.svg" alt="Back" style="transform: rotate(180deg);")
        h4.mb-0.page-title Search {{ searchTypeLabel }}

    // Search input
    .search-input-container
      .search-input-wrapper
        input.search-input(
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          :placeholder="placeholderText"
          @input="handleSearchInput"
        )
        .search-icon
          img(src="@/assets/icons/searchIcon.svg" alt="Search")

    // Loading state
    .search-results-skeleton(v-if="loading")
      .skeleton-list
        .skeleton-item(v-for="n in 8" :key="n")
          .skeleton-avatar
          .skeleton-info
            .skeleton-name
            .skeleton-description

    // Error state
    .error-container(v-else-if="error")
      .error-content
        h3 Error
        p {{ errorMessage }}
        el-button(type="primary" @click="performSearch") Retry

    // Initial state (no search performed)
    .empty-state(v-else-if="!hasSearched")
      .empty-icon
        img(src="@/assets/icons/searchIcon.svg" alt="Search")
      p.empty-text Enter a search term to find {{ searchTypeLabel.toLowerCase() }}

    // No results state
    .no-results-state(v-else-if="results.length === 0")
      .empty-icon
        img(src="@/assets/icons/searchIcon.svg" alt="No results")
      h3.no-results-title No {{ searchTypeLabel }} Found
      p.no-results-text Try a different search term

    // Results
    .results-container(v-else)
      // Stickers grid
      .stickers-grid(v-if="searchType === 'stickers'")
        .sticker-card(
          v-for="sticker in results"
          :key="sticker.id"
          @click="handleStickerClick(sticker)"
        )
          .sticker-image-wrapper
            img.sticker-image(
              :src="sticker.thumbnailUrl || sticker.url"
              :alt="sticker.title"
              @error="handleImageError($event, 'sticker')"
            )
          .sticker-info
            p.sticker-title {{ sticker.title }}
            .sticker-creator
              img.creator-avatar(
                :src="sticker.organization?.avatar || sticker.user?.avatar || (sticker.organization ? getPlaceholderImage(sticker.organization) : defaultProfileImage)"
                :alt="sticker.organization?.name || getUserName(sticker.user)"
                @error="(e) => handleImageError(e, 'avatar', sticker.organization || sticker.user)"
              )
              span.creator-name {{ sticker.organization?.name || getUserName(sticker.user) }}

      // Users/Organizations list
      .entities-list(v-else)
        .entity-item(
          v-for="entity in results"
          :key="entity.id"
          @click="handleEntityClick(entity)"
        )
          img.entity-avatar(
            :src="entity.avatar || getPlaceholderImage(entity)"
            :alt="getEntityName(entity)"
            @error="(e) => handleImageError(e, 'avatar', entity)"
          )
          .entity-info
            p.entity-name {{ getEntityName(entity) }}
          .entity-chevron
            img(src="@/assets/icons/chevron-right.svg" alt="View")

DialogDonateCause(
  v-model="showDonationDialog"
  :cause="selectedCause"
  :orgId="isUsingPersonalWallet ? null : userOrgId"
  :orgName="isUsingPersonalWallet ? 'Your' : userOrgName"
  @success="handleDonationSuccess"
)

DialogDonationSuccess(
  v-model="showSuccessDialog"
  :causeName="donatedCauseName"
)</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import { makeRequest } from '@/graphs/index';
import {
  getHomeScreenSearchData,
  unclaimedBenevityCauses,
  findOrganizationMetrics,
  benevityDetails,
} from '@/graphs/gql';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import DialogDonateCause from '../supported-causes/DialogDonateCause.vue';
import DialogDonationSuccess from '../supported-causes/DialogDonationSuccess.vue';
import defaultProfileImage from '@/assets/images/default-profile.jpeg';
import globalShared from '@/shared';
import manageShared from '@/views/manage-users-and-organizations/shared';

export default defineComponent({
  name: 'Search',
  components: {
    WebAppLayout,
    DialogDonateCause,
    DialogDonationSuccess,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const { getAuthToken, makeGraphRequest } = globalShared();
    const { getOrganizationsV2 } = manageShared();

    // State
    const searchQuery = ref('');
    const searchType = ref('stickers');
    const placeholderText = ref('Search');
    const results = ref<any[]>([]);
    const loading = ref(false);
    const error = ref(false);
    const errorMessage = ref('');
    const hasSearched = ref(false);
    const searchInputRef = ref<HTMLInputElement | null>(null);
    const searchTimeout = ref<number | null>(null);
    
    // Donation flow state
    const showDonationDialog = ref(false);
    const showSuccessDialog = ref(false);
    const donatedCauseName = ref('');
    const selectedCause = ref<any>({
      id: '',
      causeId: '',
      causeName: '',
      causeLogo: '',
      causeDescription: '',
    });
    const userOrgId = ref('');
    const userOrgName = ref('Organization');
    const userOrganizations = ref<any[]>([]);
    const hasWallet = ref(false);
    const isUsingPersonalWallet = ref(false); // Track if user has personal wallet

    // Computed
    const searchTypeLabel = computed(() => {
      const labels: { [key: string]: string } = {
        stickers: 'Stickers',
        users: 'People',
        organizations: 'Organizations',
        'non-profits': 'Non-Profits',
      };
      return labels[searchType.value] || 'Results';
    });

    // Methods
    const handleBack = () => {
      router.back();
    };

    const getUserName = (user: any) => {
      if (!user) return '';
      return `${user.firstname || ''} ${user.lastname || ''}`.trim();
    };

    const getEntityName = (entity: any) => {
      if (entity.name) return entity.name;
      return getUserName(entity);
    };

    const getPlaceholderImage = (entity: any): string => {
      // Check if entity is an organization or non-profit
      if (entity && (entity.organization_type === 'FORPROFIT'
          || entity.organization_type === 'NONPROFIT'
          || entity.name)) {
        // Return a data URI for organization placeholder
        return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23e8f5f4" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" font-size="40" fill="%231fbeaf"%3E🏢%3C/text%3E%3C/svg%3E';
      }
      // Return default profile image for users
      return defaultProfileImage;
    };

    const handleImageError = (event: Event, type: 'avatar' | 'sticker', entity?: any) => {
      const target = event.target as HTMLImageElement;
      if (!target) return;

      // Prevent infinite loop if default image also fails
      if (target.src === defaultProfileImage || target.src.startsWith('data:image/svg')) return;

      if (type === 'avatar') {
        target.src = entity ? getPlaceholderImage(entity) : defaultProfileImage;
      } else if (type === 'sticker') {
        // For stickers, use a neutral background color
        target.style.display = 'none';
        target.parentElement?.classList.add('no-image');
      }
    };

    // Check if an organization is unclaimed based on email pattern
    const isUnclaimed = (org: any) => {
      if (!org.email) return false;
      // Check if email matches pattern: unclaimed-*@spotlight.xyz
      const unclaimedPattern = /^unclaimed-(.*)@spotlight\.xyz$/i;
      return unclaimedPattern.test(org.email);
    };

    // Sort results by relevance: exact match > contains search term > alphabetically
    const sortByRelevance = (items: any[], searchTerm: string, nameGetter: (item: any) => string) => {
      const searchTermLower = searchTerm.toLowerCase().trim();
      return items.sort((a, b) => {
        const aNameLower = nameGetter(a).toLowerCase();
        const bNameLower = nameGetter(b).toLowerCase();

        // Check for exact matches
        const aIsExact = aNameLower === searchTermLower;
        const bIsExact = bNameLower === searchTermLower;
        if (aIsExact && !bIsExact) return -1;
        if (!aIsExact && bIsExact) return 1;

        // Check if name contains search term
        const aContains = aNameLower.includes(searchTermLower);
        const bContains = bNameLower.includes(searchTermLower);
        if (aContains && !bContains) return -1;
        if (!aContains && bContains) return 1;

        // Both contain or both don't contain - sort alphabetically
        return aNameLower.localeCompare(bNameLower);
      });
    };

    // Merge Spotlight and Benevity search results (matching Flutter logic)
    const mergeSearchResults = (
      spotlightOrgs: any[],
      benevityCauses: any[],
      searchTerm: string,
    ) => {
      const organizationsList: any[] = [];
      const spotlightOrgList: any[] = [];
      const addedOrgNames = new Set<string>();

      // Process spotlight organizations
      spotlightOrgs.forEach((spotlightOrg) => {
        const orgNameKey = spotlightOrg.name?.toLowerCase() || '';
        if (orgNameKey) {
          spotlightOrgList.push(spotlightOrg);
        }
      });

      // Process benevity causes and convert them to organizations
      benevityCauses.forEach((benevityCause) => {
        const benevityOrgNameKey = benevityCause.name?.toLowerCase() || '';
        let isSpotlightOrgAdded = false;

        // Check if this benevity cause exists in spotlight
        for (const spotlightOrg of spotlightOrgList) {
          if (spotlightOrg.name?.toLowerCase() === benevityOrgNameKey) {
            // If spotlight org is claimed, prefer it over benevity
            if (!isUnclaimed(spotlightOrg)) {
              organizationsList.push(spotlightOrg);
              addedOrgNames.add(benevityOrgNameKey);
              isSpotlightOrgAdded = true;
              break;
            }
          }
        }

        // If no claimed spotlight org found, add benevity cause as organization
        if (!isSpotlightOrgAdded && !addedOrgNames.has(benevityOrgNameKey)) {
          const convertedOrg = {
            id: '',
            benevityId: benevityCause.id, // Keep benevityId for click navigation
            causeId: benevityCause.causeId || '',
            name: benevityCause.name || '',
            email: '',
            avatar: benevityCause.logo || '',
            background: null,
            organization_type: 'NONPROFIT',
            description: benevityCause.description || '',
          };
          organizationsList.push(convertedOrg);
          addedOrgNames.add(benevityOrgNameKey);
        }
      });

      // Add remaining spotlight organizations that weren't already added
      spotlightOrgList.forEach((spotlightOrg) => {
        const orgNameKey = spotlightOrg.name?.toLowerCase() || '';
        if (!addedOrgNames.has(orgNameKey)) {
          organizationsList.push(spotlightOrg);
        }
      });

      // Sort by relevance using helper function
      return sortByRelevance(organizationsList, searchTerm, (org) => org.name || '');
    };

    const performSearch = async () => {
      const query = searchQuery.value.trim();
      if (!query) {
        hasSearched.value = false;
        results.value = [];
        return;
      }

      console.log('[Search] Performing search for:', query, 'Type:', searchType.value);
      loading.value = true;
      error.value = false;
      hasSearched.value = true;

      try {
        // For non-profits, search both Spotlight and Benevity in parallel
        if (searchType.value === 'non-profits') {
          const [spotlightResponse, benevityResponse] = await Promise.all([
            makeRequest(
              'query',
              getHomeScreenSearchData,
              { query },
              null,
            ),
            makeRequest(
              'query',
              unclaimedBenevityCauses,
              { query, page: '1' },
              null,
            ),
          ]);

          // Check for errors in Spotlight response
          if (spotlightResponse.errors) {
            throw new Error(spotlightResponse.errors[0]?.message || 'Search failed');
          }

          const spotlightData = spotlightResponse.data?.getHomeScreenSearchData;
          const benevityData = benevityResponse.data?.unclaimedBenevityCauses;

          // Extract spotlight non-profit organizations
          const spotlightOrgs = (spotlightData?.organizationsData || [])
            .filter((org: any) => org.organization_type === 'NONPROFIT');

          // Extract benevity causes
          const benevityCauses = benevityData?.causes || [];

          // Merge and deduplicate results with relevance sorting
          results.value = mergeSearchResults(spotlightOrgs, benevityCauses, query);
          console.log('[Search] Found', results.value.length, 'non-profit results');
        } else {
          // For other search types, use single query (existing logic)
          const response = await makeRequest(
            'query',
            getHomeScreenSearchData,
            { query },
            null,
          );

          if (response.errors) {
            throw new Error(response.errors[0]?.message || 'Search failed');
          }

          const data = response.data?.getHomeScreenSearchData;
          if (!data) {
            throw new Error('No data returned from search');
          }

          // Filter results based on search type and apply relevance sorting
          switch (searchType.value) {
            case 'stickers':
              results.value = sortByRelevance(
                data.stickersData || [],
                query,
                (sticker) => sticker.title || '',
              );
              break;
            case 'users':
              results.value = sortByRelevance(
                data.usersData || [],
                query,
                (user) => getUserName(user),
              );
              break;
            case 'organizations':
              // Filter for-profit organizations and sort by relevance
              results.value = sortByRelevance(
                (data.organizationsData || [])
                  .filter((org: any) => org.organization_type === 'FORPROFIT'),
                query,
                (org) => org.name || '',
              );
              break;
            default:
              results.value = [];
          }
          console.log('[Search] Found', results.value.length, 'results');
        }
      } catch (err: any) {
        console.error('[Search] Error during search:', err);
        error.value = true;
        errorMessage.value = err.message || 'An error occurred while searching';
        results.value = [];
      } finally {
        loading.value = false;
      }
    };

    const handleSearchInput = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      searchTimeout.value = window.setTimeout(() => {
        performSearch();
      }, 500);
    };

    const handleStickerClick = (sticker: any) => {
      router.push(`/web/sticker/${sticker.id}`);
    };

    const fetchUserOrganizations = async () => {
      try {
        console.log('Fetching user organizations for donation...');
        const baseInput = {
          ids: null,
          entity: null,
          entityIds: null,
          isActive: null,
          statuses: 'Approved',
          query: null,
          page: 1,
          pageSize: 50,
          excludeUnclaimed: true,
        };

        const result = await getOrganizationsV2(baseInput);
        userOrganizations.value = result || [];

        // Priority: Use orgId from URL query params (from Supported Causes page)
        // This ensures we use the same org that was selected on Supported Causes
        if (userOrganizations.value.length > 0) {
          const orgIdParam = route.query.orgId as string;
          const savedOrg = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
          
          if (orgIdParam) {
            // Use org from URL (user came from Supported Causes)
            const matchingOrg = userOrganizations.value.find((org: any) => org.id === orgIdParam);
            if (matchingOrg) {
              userOrgId.value = matchingOrg.id;
              userOrgName.value = matchingOrg.name;
              console.log('Using org from URL query:', userOrgName.value);
              return;
            }
          }
          
          if (savedOrg && userOrganizations.value.find((org: any) => org.id === savedOrg.id)) {
            // Use saved org from store
            userOrgId.value = savedOrg.id;
            userOrgName.value = savedOrg.name;
            console.log('Using org from store:', userOrgName.value);
          } else {
            // Fallback to first org
            userOrgId.value = userOrganizations.value[0].id;
            userOrgName.value = userOrganizations.value[0].name;
            console.log('Using first org:', userOrgName.value);
          }
        }
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      }
    };

    const checkWallet = async () => {
      console.log('[WALLET CHECK] ========== START ==========');
      console.log('[WALLET CHECK] userOrgId:', userOrgId.value);
      
      // CRITICAL FIX: Check USER wallet first, not organization wallet
      // User may have a personal wallet for donations
      try {
        console.log('[WALLET CHECK] Checking USER wallet first...');
        const userWalletResponse = await makeGraphRequest(
          'query',
          benevityDetails,
          {},
          getAuthToken(),
        );

        console.log('[WALLET CHECK] User wallet response:', userWalletResponse);
        
        if (userWalletResponse?.data?.benevityDetails?.hasWallet) {
          const userWallet = userWalletResponse.data.benevityDetails;
          hasWallet.value = userWallet.hasWallet || false;
          isUsingPersonalWallet.value = true; // ← Mark that we're using personal wallet
          console.log('[WALLET CHECK] ✓ User has PERSONAL wallet:', hasWallet.value);
          console.log('[WALLET CHECK] Amount in wallet:', userWallet.amountInWallet);
          console.log('[WALLET CHECK] ========== END (USER WALLET) ==========');
          return;
        }
        
        console.log('[WALLET CHECK] User does NOT have personal wallet, checking organization...');
        isUsingPersonalWallet.value = false;
      } catch (error) {
        console.error('[WALLET CHECK] Error checking user wallet:', error);
        isUsingPersonalWallet.value = false;
      }

      // Fallback: Check organization wallet if user doesn't have personal wallet
      if (!userOrgId.value) {
        console.log('[WALLET CHECK] No userOrgId available');
        hasWallet.value = false;
        console.log('[WALLET CHECK] ========== END (NO WALLET) ==========');
        return;
      }

      try {
        console.log('[WALLET CHECK] Checking ORGANIZATION wallet for orgId:', userOrgId.value);
        const sanitizeOrgId = (id: string) => id.replace(/[^\w-]/g, '');
        const orgId = sanitizeOrgId(userOrgId.value);
        const startDate = dayjs().subtract(30, 'days').format('YYYY-MM-DD');
        const endDate = dayjs().format('YYYY-MM-DD');

        const query = findOrganizationMetrics(orgId, startDate, endDate);

        const response = await makeGraphRequest(
          'query',
          query,
          {},
          getAuthToken(),
        );

        console.log('[WALLET CHECK] Org wallet response:', response?.data?.findOrganizationMetrics?.moneyLeftInTheAccount);

        if (response?.data?.findOrganizationMetrics?.moneyLeftInTheAccount) {
          const walletData = response.data.findOrganizationMetrics.moneyLeftInTheAccount;
          hasWallet.value = walletData.hasWallet || false;
          console.log('[WALLET CHECK] Organization wallet status:', hasWallet.value);
          console.log('[WALLET CHECK] Amount in wallet:', walletData.amountInWallet);
        } else {
          hasWallet.value = false;
          console.log('[WALLET CHECK] Organization does NOT have wallet');
        }
      } catch (error) {
        console.error('[WALLET CHECK] Failed to load org wallet:', error);
        hasWallet.value = false;
      }
      
      console.log('[WALLET CHECK] ========== END (FINAL hasWallet:', hasWallet.value, ') ==========');
    };

    const handleEntityClick = async (entity: any) => {
      // Check if entity has either id or benevityId
      const entityId = entity.id || entity.benevityId;
      if (!entityId) {
        console.warn('Entity has no ID:', entity);
        return;
      }

      console.log('=== ENTITY CLICKED ===');
      console.log('Entity:', entity);
      console.log('ID:', entityId);
      console.log('Type:', searchType.value);
      console.log('====================');

      if (searchType.value === 'users') {
        router.push(`/web/profile/user/${entityId}`);
      } else if (searchType.value === 'organizations') {
        // For organizations (for-profit), navigate to profile
        router.push(`/web/profile/organization/${entityId}`);
      } else if (searchType.value === 'non-profits') {
        console.log('========================================');
        console.log('NON-PROFIT CLICKED - DONATION FLOW START');
        console.log('========================================');
        console.log('Selected Non-Profit:', entity.name);
        console.log('Non-Profit ID:', entityId);
        
        // Ensure we have user's organization loaded
        if (!userOrgId.value) {
          console.log('User org not loaded, fetching...');
          await fetchUserOrganizations();
        }
        
        // Validate we have an organization
        if (!userOrgId.value) {
          console.error('❌ CRITICAL: No organization available for donation');
          console.error('User must have an organization to donate');
          return;
        }
        
        console.log('✓ User Organization:', userOrgName.value, '(', userOrgId.value, ')');
        console.log('');
        console.log('--- CHECKING WALLET STATUS ---');
        
        // Check wallet for the selected organization
        await checkWallet();
        
        console.log('');
        console.log('--- WALLET CHECK RESULT ---');
        console.log('Has Wallet:', hasWallet.value ? '✓ YES' : '✗ NO');
        
        if (!hasWallet.value) {
          // Navigate to create wallet if no wallet exists
          console.log('');
          console.log('⚠️  NO WALLET FOUND');
          console.log('Redirecting to: /web/wallet/create');
          console.log('Query params: { orgId:', userOrgId.value, '}');
          console.log('');
          console.log('NOTE: If user has a PERSONAL wallet, this might be incorrect!');
          console.log('      The system should check USER wallet first, not ORG wallet.');
          console.log('========================================');
          
          router.push({
            path: '/web/wallet/create',
            query: { orgId: userOrgId.value },
          });
          return;
        }
        
        // Convert entity to cause format
        selectedCause.value = {
          id: entity.id || '',
          causeId: entity.causeId || entityId,
          causeName: entity.name || '',
          causeLogo: entity.avatar || '',
          causeDescription: entity.description || '',
        };
        
        console.log('');
        console.log('✓ WALLET FOUND - OPENING DONATION DIALOG');
        console.log('Cause:', selectedCause.value.causeName);
        console.log('Donating from:', userOrgName.value);
        console.log('========================================');
        
        showDonationDialog.value = true;
      }
    };

    const handleDonationSuccess = (data: any) => {
      console.log('=== DONATION SUCCESS HANDLER ===');
      console.log('Donation data:', data);
      console.log('===============================');
      
      // Store the cause name and show success dialog
      donatedCauseName.value = data.cause.causeName;
      showSuccessDialog.value = true;
    };

    // Watch for orgId changes in URL query params
    watch(() => route.query.orgId, (newOrgId) => {
      if (newOrgId && newOrgId !== userOrgId.value) {
        console.log('=== ORG ID CHANGED IN URL ===');
        console.log('New orgId:', newOrgId);
        
        // Update selected org if it exists in user's organizations
        const matchingOrg = userOrganizations.value.find((org: any) => org.id === newOrgId);
        if (matchingOrg) {
          userOrgId.value = matchingOrg.id;
          userOrgName.value = matchingOrg.name;
          console.log('Updated to org:', userOrgName.value);
        } else if (userOrganizations.value.length === 0) {
          // Organizations not loaded yet, will be set by fetchUserOrganizations
          console.log('Organizations not loaded yet, will be set on load');
        }
        console.log('============================');
      }
    });

    // Lifecycle
    onMounted(async () => {
      console.log('[Search] Component mounted');
      console.log('[Search] Viewport width:', window.innerWidth);
      console.log('[Search] Max-width constraint: 1400px applied to sections');
      
      // Load user organizations for donation functionality
      // This ensures orgId from URL (if navigating from Supported Causes) is properly set
      await fetchUserOrganizations();
      
      // Get initial query params from URL
      const query = route.query.q as string;
      const type = route.query.type as string;
      const hint = route.query.hint as string;

      if (query) {
        searchQuery.value = query;
      }

      if (type) {
        searchType.value = type;
      }

      if (hint) {
        placeholderText.value = hint;
      } else {
        placeholderText.value = `Search ${searchTypeLabel.value}`;
      }

      // Perform initial search if query is provided
      if (query) {
        performSearch();
      }

      // Focus input after mount
      setTimeout(() => {
        searchInputRef.value?.focus();
        // Check actual rendered width
        const searchHeader = document.querySelector('.search-header');
        if (searchHeader) {
          console.log('[Search] Search header actual width:', searchHeader.clientWidth, 'px');
        }
      }, 100);
    });

    return {
      searchQuery,
      searchType,
      placeholderText,
      results,
      loading,
      error,
      errorMessage,
      hasSearched,
      searchInputRef,
      searchTypeLabel,
      defaultProfileImage,
      showDonationDialog,
      showSuccessDialog,
      donatedCauseName,
      selectedCause,
      userOrgId,
      userOrgName,
      isUsingPersonalWallet,
      handleBack,
      getUserName,
      getEntityName,
      getPlaceholderImage,
      handleImageError,
      performSearch,
      handleSearchInput,
      handleStickerClick,
      handleEntityClick,
      handleDonationSuccess,
    };
  },
});
</script>

<style lang="scss" scoped>
.search-page {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding-bottom: 2rem;
}

.search-header {
  // No max-width - goes full width
  padding: 20px var(--web-app-padding-mobile);
  background-color: white;
  border-bottom: 1px solid #e0e0e0;

  @media (min-width: 768px) {
    padding: 20px var(--web-app-padding-tablet);
  }
  
  @media (min-width: 1024px) {
    padding: 20px var(--web-app-padding-desktop);
  }
}

.back-button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  img {
    width: 20px;
    height: 20px;
    opacity: 0.6;
  }
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #000;
}

.search-input-container {
  // No max-width - goes full width
  padding: 16px var(--web-app-padding-mobile);
  background-color: white;
  border-bottom: 1px solid #e0e0e0;

  @media (min-width: 768px) {
    padding: 16px var(--web-app-padding-tablet);
  }
  
  @media (min-width: 1024px) {
    padding: 16px var(--web-app-padding-desktop);
  }
}

.search-input-wrapper {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  height: 48px;
  padding: 0 48px 0 1rem;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 1rem;
  background-color: #edf3f7;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1fbeaf;
  }

  &:focus {
    outline: none;
    border-color: #1fbeaf;
    box-shadow: 0 0 0 2px rgba(31, 190, 175, 0.1);
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }
}

.search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  img {
    width: 20px;
    height: 20px;
    opacity: 0.6;
  }
}

.search-results-skeleton {
  padding: var(--web-app-padding-mobile);

  @media (min-width: 768px) {
    padding: var(--web-app-padding-tablet);
  }
  
  @media (min-width: 1024px) {
    padding: var(--web-app-padding-desktop);
  }
}

.skeleton-list {
  background: white;
  border-radius: 12px;
  overflow: hidden;
}

.skeleton-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e0e0e0 50%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  flex-shrink: 0;
}

.skeleton-info {
  flex: 1;
  min-width: 0;
}

.skeleton-name {
  width: 60%;
  height: 16px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e0e0e0 50%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-description {
  width: 40%;
  height: 12px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e0e0e0 50%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  animation-delay: 0.1s;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #1fbeaf;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 1rem;
  color: #666;
  font-size: 1rem;
}

.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.error-content {
  text-align: center;
  max-width: 400px;

  h3 {
    color: #f56c6c;
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  p {
    color: #666;
    margin-bottom: 2rem;
  }
}

.empty-state,
.no-results-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin-bottom: 1.5rem;
  opacity: 0.3;

  img {
    width: 100%;
    height: 100%;
  }
}

.empty-text {
  color: #666;
  font-size: 1rem;
}

.no-results-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}

.no-results-text {
  color: #666;
  font-size: 1rem;
}

.results-container {
  // No max-width - constrained by parent WebAppLayout
  padding: var(--web-app-padding-mobile);

  @media (min-width: 768px) {
    padding: var(--web-app-padding-tablet);
  }
  
  @media (min-width: 1024px) {
    padding: var(--web-app-padding-desktop);
  }
}

// Stickers Grid
.stickers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.sticker-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.sticker-image-wrapper {
  width: 100%;
  padding-top: 100%;
  position: relative;
  background-color: #f5f5f5;

  &.no-image {
    background: linear-gradient(135deg, #e8f5f4 0%, #d4ebe9 100%);
    display: flex;
    align-items: center;
    justify-content: center;

    &::after {
      content: '🎯';
      font-size: 3rem;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      opacity: 0.3;
    }
  }
}

.sticker-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sticker-info {
  padding: 12px;
}

.sticker-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sticker-creator {
  display: flex;
  align-items: center;
  gap: 6px;
}

.creator-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
}

.creator-name {
  font-size: 0.75rem;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Entities List
.entities-list {
  background: white;
  border-radius: 12px;
  overflow: hidden;
}

.entity-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9f9f9;
  }
}

.entity-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.entity-info {
  flex: 1;
  min-width: 0;
}

.entity-name {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entity-chevron {
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  img {
    width: 100%;
    height: 100%;
    opacity: 0.4;
  }
}
</style>
